const SELECTORS = {
  trigger: "[data-hm-trigger]",
  panel: "[data-hm-panel]",
};

const HM_OPEN_EVENT = "hm:open";
const HEADER_SELECTOR = "header.section-header, header[class*='section-header']";

// Hover-Open/Close: kurze Verzögerungen, damit das Panel beim Drüberfahren
// reaktiv wirkt, aber nicht bei jedem zufälligen Vorbeischieben aufgeht.
const HOVER_OPEN_DELAY_MS = 60;
const HOVER_CLOSE_DELAY_MS = 150;

class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSiblingOpen = this.onSiblingOpen.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.hoverOpenTimer = null;
    this.hoverCloseTimer = null;
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[HeaderMenu:init]", error);
    }
  }

  init() {
    this.#bindElements();
    this.#validateElements();
    this.#bindEvents();
    this.#setOpen(false, { skipAnim: true });
  }

  #isHoverMode() {
    if (this.dataset.hmOrientation !== "horizontal") return false;
    if (this.dataset.hmTriggerMode !== "hover") return false;
    // Touch-/Coarse-Pointer-Geräte fallen automatisch auf Click zurück —
    // Hover-Open ist dort wegen Synthetic-Hover-Events nicht zuverlässig.
    if (
      typeof window.matchMedia === "function" &&
      !window.matchMedia("(hover: hover) and (pointer: fine)").matches
    ) {
      return false;
    }
    return true;
  }

  #bindElements() {
    this.trigger = this.querySelector(SELECTORS.trigger);
    this.panel = this.querySelector(SELECTORS.panel);
    this.header = this.closest(HEADER_SELECTOR) || this.closest("header");
  }

  #validateElements() {
    if (!this.trigger) {
      throw new Error("[HeaderMenu] Missing [data-hm-trigger]");
    }
    if (!this.panel) {
      throw new Error("[HeaderMenu] Missing [data-hm-panel]");
    }
  }

  #bindEvents() {
    this.trigger.addEventListener("click", this.#onTriggerClick.bind(this));
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener(HM_OPEN_EVENT, this.onSiblingOpen);
    window.addEventListener("resize", this.onResize);
    this.panel.addEventListener("transitionend", this.onTransitionEnd);

    // Hover-Modus: pointerenter auf der gesamten <header-menu>-Hülle (Trigger
    // + Panel) öffnet, pointerleave schließt mit kleiner Karenz, damit der
    // User vom Trigger ins Panel wandern kann, ohne dass es zwischendurch
    // wegschnappt.
    if (this.#isHoverMode()) {
      // Listener nur auf <header-menu> selbst — pointerenter/leave nutzen
      // Hit-Testing über den DOM-Subtree, decken also Trigger, Bridge (::after)
      // und Panel automatisch ab. Separate Listener auf .panel würden beim
      // Wechsel Panel→Trigger fälschlich pointerleave feuern und das Menü
      // schließen, obwohl die Maus noch im Subtree ist.
      this.addEventListener("pointerenter", this.onPointerEnter);
      this.addEventListener("pointerleave", this.onPointerLeave);
    }
  }

  #clearHoverTimers() {
    if (this.hoverOpenTimer) {
      clearTimeout(this.hoverOpenTimer);
      this.hoverOpenTimer = null;
    }
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }

  onPointerEnter(event) {
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (!this.#isHoverMode()) return;
    this.#clearHoverTimers();
    if (this.classList.contains("is-open")) return;
    this.hoverOpenTimer = setTimeout(() => {
      this.hoverOpenTimer = null;
      this.open();
    }, HOVER_OPEN_DELAY_MS);
  }

  onPointerLeave(event) {
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (!this.#isHoverMode()) return;
    this.#clearHoverTimers();
    if (!this.classList.contains("is-open")) return;
    this.hoverCloseTimer = setTimeout(() => {
      this.hoverCloseTimer = null;
      this.close();
    }, HOVER_CLOSE_DELAY_MS);
  }

  open() {
    this.#clearHoverTimers();
    this.#setOpen(true);
    document.dispatchEvent(
      new CustomEvent(HM_OPEN_EVENT, { detail: { source: this } }),
    );
  }

  close() {
    this.#clearHoverTimers();
    this.#setOpen(false);
  }

  #setOpen(value, { skipAnim = false } = {}) {
    this.trigger.setAttribute("aria-expanded", String(value));

    if (this.dataset.hmOrientation !== "horizontal") {
      this.classList.toggle("is-open", value);
      if (value) {
        this.panel.removeAttribute("hidden");
      } else {
        this.panel.setAttribute("hidden", "");
      }
      return;
    }

    if (value) {
      this.#positionPanel();
      this.panel.removeAttribute("hidden");
      if (skipAnim) {
        this.classList.add("is-open");
      } else {
        // force reflow before adding class so transition kicks in
        // eslint-disable-next-line no-unused-expressions
        this.panel.offsetWidth;
        requestAnimationFrame(() => {
          this.classList.add("is-open");
        });
      }
    } else {
      this.classList.remove("is-open");
      if (skipAnim) {
        this.panel.setAttribute("hidden", "");
      }
      // hidden gets re-applied in onTransitionEnd
    }
  }

  #positionPanel() {
    if (this.dataset.hmOrientation !== "horizontal") return;
    const headerEl = this.header || document.querySelector("header");
    if (!headerEl) return;
    const headerRect = headerEl.getBoundingClientRect();
    this.panel.style.setProperty("--hm-panel-top", `${headerRect.bottom}px`);

    // Bridge-Höhe = Distanz von Trigger-Unterkante bis Panel-Oberkante.
    // Deckt das padding zwischen Nav-Row und Header-Unterkante ab, damit
    // ein langsamer Maus-Übergang vom Trigger ins Panel nicht pointerleave
    // auslöst.
    const triggerRect = this.trigger.getBoundingClientRect();
    const bridgeHeight = Math.max(0, headerRect.bottom - triggerRect.bottom);
    this.style.setProperty("--hm-bridge-height", `${bridgeHeight}px`);
  }

  onTransitionEnd(event) {
    if (event.target !== this.panel) return;
    if (event.propertyName !== "transform" && event.propertyName !== "opacity") return;
    if (!this.classList.contains("is-open")) {
      this.panel.setAttribute("hidden", "");
    }
  }

  onResize() {
    if (this.classList.contains("is-open")) {
      this.#positionPanel();
    }
  }

  #onTriggerClick(event) {
    event.preventDefault();
    if (this.classList.contains("is-open")) {
      this.close();
    } else {
      this.open();
    }
  }

  onSiblingOpen(event) {
    if (event.detail?.source !== this) this.close();
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.classList.contains("is-open")) {
      this.close();
      this.trigger.focus();
    }
  }

  onDocumentClick(event) {
    if (!this.classList.contains("is-open")) return;
    if (this.contains(event.target)) return;
    if (this.panel.contains(event.target)) return;
    this.close();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener(HM_OPEN_EVENT, this.onSiblingOpen);
    window.removeEventListener("resize", this.onResize);
    this.panel.removeEventListener("transitionend", this.onTransitionEnd);
    this.removeEventListener("pointerenter", this.onPointerEnter);
    this.removeEventListener("pointerleave", this.onPointerLeave);
    this.#clearHoverTimers();
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}
